import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT, NAV_LINKS } from "../../lib/data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed inset-x-0 top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-xl transition-shadow duration-500 ${
          scrolled ? "shadow-[0_4px_20px_rgba(16,20,24,0.06)]" : ""
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
          <Link to="/" aria-label="LendX Home" data-testid="nav-logo-link">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-7 xl:flex" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold tracking-wide transition-colors duration-300 ${
                    isActive ? "text-orange-deep" : "text-gray-600 hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-call-btn"
              className="flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-[13px] font-medium text-gray-700 transition-colors duration-300 hover:border-orange hover:text-orange-deep"
            >
              <Phone size={14} className="text-gold-deep" />
              {CONTACT.phone}
            </a>
            <button
              onClick={() => navigate("/contact")}
              data-testid="get-funded-btn"
              className="group flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-bold text-inkx transition-transform duration-300 hover:scale-[1.04]"
            >
              Get Funded
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <button
            className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 text-navy xl:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-white"
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between border-b border-gray-200 px-5">
              <Logo />
              <button
                className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 text-navy"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-btn"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block border-b border-gray-100 py-4 font-heading text-3xl font-semibold tracking-tight transition-colors ${
                        isActive ? "text-orange-deep" : "text-navy hover:text-orange-deep"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.45 }}
                className="pt-8"
              >
                <button
                  onClick={() => navigate("/contact")}
                  data-testid="mobile-get-funded-btn"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-bold text-inkx"
                >
                  Get Funded <ArrowUpRight size={16} />
                </button>
                <a href={CONTACT.phoneHref} data-testid="mobile-call-btn" className="mt-4 block text-center text-sm text-gray-500">
                  {CONTACT.phone}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
